/**
 * CompactVqeContent — the body of the Compact VQE popover. A dense, single-panel
 * editor over the SAME SR-Edit pipeline as the full VQE. Prim #1 §1 pins the
 * chrome so the footer is never clipped: a THREE-region flex column —
 *   (a) a pinned header (readiness pip · summary + the UPDATE / DELETE
 *       "No WHERE" badge · close · drill breadcrumb),
 *   (b) a `min-h-0 flex-1` SCROLLING body (`ScrollFade`) holding ONLY the clause
 *       sections, so long statements scroll here (with a bottom fade cue) while
 *       the chrome stays put, and
 *   (c) a PINNED bottom block (SIBLING of the scroll body, never inside it)
 *       carrying the universal live SQL preview + the `CompactVqeFooter`.
 *
 * DDL takes a different shape: `ddlBody` (the compact Visual DDL editor) IS
 * regions (b)+(c) — it brings its own scroll body and its own single pinned
 * action row — so it replaces both. That is why a DDL popup has no SQL
 * preview and no bottom "Close" row; the header × closes it.
 *
 * All dispatch state (the model, the drill/degrade/DDL branch selection, the
 * preview text, and `showOpenFull`) comes from ONE source — `useCompactBodyState`
 * — so the scrolling body and the pinned footer never disagree about which card
 * owns the single "Open full editor" CTA (§1.5).
 *
 * Buffer-as-source-of-truth: every add / edit / remove inside the sections
 * commits straight to the Monaco buffer via the shared `editContext`. When the
 * statement is a shape the compact surface can't safely edit (set op, named
 * window fn, subquery, INSERT, MERGE, or a CTE on UPDATE/DELETE — a CTE on
 * SELECT stays editable), it degrades to a one-line summary +
 * "Open full editor" instead of broken controls. When the cursor statement is
 * DDL, `ddlCard` wins the whole dispatch and the DDL summary card renders.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS3;
 * Plan 9 §2.9/S13 for the DDL card.
 */

import { ChevronLeft, SquarePen, Trash2, X } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';

import { CteAddPopover } from '../render/select/CteAddPopover';
import { DmlNoWhereBadge } from '../render/common/DmlNoWhereBadge';
import { dmlWhereScope } from '../render/common/dmlWhereScope';
import { ScrollFade } from '../render/common/ScrollFade';
import { VqeTooltip } from '../render/common/VqeTooltip';
import { EngineIdentityBadge } from '../ux/EngineIdentityBadge';
import { VqeReadinessBanner } from '../ux/VqeReadinessBanner';
import type { VqeReadinessState } from '../ux/VqeReadinessBanner';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { ParseOptions } from '../../../sql-grammar/model';
import type { VqeEditContext } from '../edit/editContext';
import { supportsMerge } from '../dialect/statementCapabilities';
import { extractMergeOutline } from '../render/merge/extractMergeOutline';
import type { RenderMode } from '../types';
import type { DdlCardModel } from './compactDdlCard';
import type { CompactDrill } from './useCompactDrill';
import { useCompactBodyState } from './useCompactBodyState';
import { CompactDmlHeaderTarget } from './CompactDmlHeaderTarget';
import { CompactDdlCard } from './sections/CompactDdlCard';
import { CompactDegradeNotice } from './sections/CompactDegradeNotice';
import { CompactDeleteSections } from './sections/CompactDeleteSections';
import { CompactEditSections } from './sections/CompactEditSections';
import { CompactMergeUnsupportedNotice } from './sections/CompactMergeUnsupportedNotice';
import {
  buildCompactSubqueryCtx,
  CompactNestedSections,
} from './sections/CompactNestedSections';
import { CompactSqlPreview } from './sections/CompactSqlPreview';
import { CompactUpdateSections } from './sections/CompactUpdateSections';
import { CompactVqeFooter } from './sections/CompactVqeFooter';
import { vqeCopy } from '../ux/vqeCopy';

export interface CompactVqeContentProps {
  /** Header summary, e.g. "SELECT from customers (c)". */
  readonly summary: string;
  /** Render mode of the cursor statement (drives degrade detection). */
  readonly mode: RenderMode | null;
  /** Parse options (engine) for the cursor statement's dialect dispatch. */
  readonly options: ParseOptions;
  /**
   * The wired edit context for the cursor statement, or null when the schema
   * snapshot has not loaded yet (controls render disabled with a reason).
   */
  readonly editContext: VqeEditContext | null;
  /** Open the full Visual Query Editor workspace seeded with this statement. */
  readonly onOpenFull: () => void;
  /** Close the popover (edits already committed to the buffer). */
  readonly onClose: () => void;
  /** Readiness state driving the leading pip banner (`ready` hides it). */
  readonly bannerState: VqeReadinessState;
  /** The engine's error message when the schema failed to load (pip tooltip). */
  readonly bannerDetail?: string;
  /** Whether the readiness pip was dismissed this session (per-tab). */
  readonly bannerDismissed: boolean;
  readonly onBannerDismiss: () => void;
  /** Omitted for read-only — the footer's standing "Open full editor" owns the CTA. */
  readonly onBannerPrimaryAction?: () => void;
  /** Local (compact-only) subquery drill stack, owned by the popover. */
  readonly drill: CompactDrill;
  /** Tab id the drill wiring attributes pushed entries to. */
  readonly tabId: string;
  /**
   * DDL summary-card model when the cursor statement is DDL (§2.9 S13);
   * `null`/absent for SELECT/DML statements. Wins over the drill/DML/degrade
   * dispatch chain below when present.
   */
  readonly ddlCard?: DdlCardModel | null;
  /**
   * Editable DDL body (the compact Visual DDL editor) supplied by the host
   * whenever `ddlCard` is set. Wins over the read-only `CompactDdlCard`
   * fallback below; omit it (or pass `null`) to keep the card. It is mounted
   * as the popover's `min-h-0 flex-1` region and owns BOTH its scroll body and
   * its single pinned action row, so it replaces the (c) footer block too.
   */
  readonly ddlBody?: ReactNode;
  /** DDL card's Edit-as-SQL action. Unused unless `ddlCard` is set. */
  readonly onEditAsSql?: () => void;
  /**
   * Gates the DDL card's Design-table button (Task 4) — threaded straight
   * through to `CompactDdlCard`, which disables the button with a
   * specific-reason tooltip when this is nullish.
   */
  readonly connectionId?: string | null;
  /** DDL card's Design-table hand-off. Unused unless `ddlCard.designTable` is set. */
  readonly onDesignTable?: () => void;
  /**
   * Resolved engine for the popover's connection, for the sticky-header
   * `EngineIdentityBadge` (A6/DML-31/DML-34) — `undefined` when disconnected,
   * which renders the badge's own "Not connected" ghost rather than a
   * misleading default engine.
   */
  readonly engineId?: DatabaseEngineId;
  /** Active database name for the same badge, when known. */
  readonly database?: string;
  /** Connection alias for the same badge (the host tab's `connectionInfo`), when known. */
  readonly connectionName?: string;
  /** Connection host — the badge's fallback label when the connection has no alias. */
  readonly serverHost?: string;
  /**
   * Hides the footer's "Open full editor" / Done row. The add-tile dialog's
   * embedded Visual pane sets this: there the dialog owns confirmation
   * (Preview / Add tile) and "Done" or a dialog-closing full-editor jump next
   * to them only misleads. The SQL preview + the degrade cards' own escape
   * CTAs are unaffected.
   */
  readonly hideFooterActions?: boolean;
  /**
   * "Run on change" footer switch (Query Editor host only): re-run the SELECT
   * after every committed visual edit. Omitted on hosts with no execution
   * surface of their own (the add-tile dialog's embedded pane).
   */
  readonly runOnChange?: { readonly enabled: boolean; readonly onToggle: () => void };
  /** The "Edit applied · Undo" status for the footer (Query Editor host only). */
  readonly editStatus?: ReactNode;
}

export function CompactVqeContent({
  summary,
  mode,
  options,
  editContext,
  onOpenFull,
  onClose,
  bannerState,
  bannerDetail,
  bannerDismissed,
  onBannerDismiss,
  onBannerPrimaryAction,
  drill,
  tabId,
  ddlCard = null,
  ddlBody = null,
  onEditAsSql = () => {},
  connectionId,
  onDesignTable = () => {},
  engineId,
  database,
  connectionName,
  serverHost,
  hideFooterActions = false,
  runOnChange,
  editStatus,
}: CompactVqeContentProps) {
  const body = useCompactBodyState({ editContext, mode, options, ddlCard, drill, tabId, onOpenFull });

  // UPDATE / DELETE: the header carries the write accent (icon + amber / red,
  // same tones as the full view's target row). When it also names the target
  // ("UPDATE orders" — anything past the bare verb), the sections' own
  // `UPDATE orders` banner row would repeat it, so they drop it.
  const dmlIntent = !ddlCard && (mode === 'update' || mode === 'delete') ? mode : null;
  const headerNamesTarget = dmlIntent !== null && summary.trim().includes(' ');
  const { model, preview, active, drilledCtx, baseSubqueryCtx, ddlCardVisible, showOpenFull, openFullWindowHint } = body;
  // The header is the popover's target row, so the red "No WHERE" badge sits
  // beside it (same detector as the Visual view's target row).
  const dmlOutline = dmlIntent === 'update' ? model.updateOutline : dmlIntent === 'delete' ? model.deleteOutline : null;
  // The header's target name doubles as the Change table picker.
  const headerDml =
    headerNamesTarget && dmlIntent === 'update' && model.updateOutline
      ? { intent: 'update' as const, outline: model.updateOutline }
      : headerNamesTarget && dmlIntent === 'delete' && model.deleteOutline
        ? { intent: 'delete' as const, outline: model.deleteOutline }
        : null;
  const summaryClass =
    dmlIntent === 'delete'
      ? 'flex min-w-0 items-center gap-1.5 font-medium text-destructive'
      : dmlIntent === 'update'
        ? 'flex min-w-0 items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400'
        : 'font-medium truncate';
  const summaryIcon =
    dmlIntent === 'delete' ? (
      <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    ) : dmlIntent === 'update' ? (
      <SquarePen className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    ) : null;

  // EDG-01/03 — a MERGE on an engine with no MERGE statement at all
  // (mysql/sqlite) degrades to the DEDICATED unsupported-MERGE notice instead
  // of the generic `CompactDegradeNotice`: only computed for that exact case
  // (resolved statement + live edit context + `!supportsMerge`), so a MERGE on
  // a supporting engine (pg 15+/mssql/oracle) is unaffected and keeps showing
  // the structure-aware generic degrade card.
  const mergeOutline = useMemo(() => {
    if (mode !== 'merge' || !model.statement || supportsMerge(options.engine)) return null;
    return extractMergeOutline(model.bufferText, model.statement.range, options);
  }, [mode, model.statement, model.bufferText, options]);

  // The DDL card suppresses the leading readiness pip except `no-connection`:
  // `read-only` (the mode every DDL statement classifies to) would otherwise
  // duplicate the card's own Edit-as-SQL with a second, differently-worded one.
  const showReadinessPip = !ddlCardVisible || bannerState === 'no-connection';

  return (
    <div className="flex min-h-0 flex-1 flex-col text-xs" data-testid="vqe-compact-popover">
      {/* (a) Pinned header — never scrolls. `sticky top-0` is retained (harmless
          on a non-scrolling flex child) so the region reads as the top chrome. */}
      <div className="sticky top-0 z-10 flex shrink-0 flex-col gap-2 border-b border-border bg-popover px-4 pb-2 pt-2">
        {/* Leading readiness pip — surfaces why editing is disabled, once.
            Suppressed while the DDL card owns `read-only` (its own Edit-as-SQL
            button already covers that; decision 4, §1.5). */}
        {showReadinessPip ? (
          <VqeReadinessBanner
            variant="pip"
            state={bannerState}
            detail={bannerDetail}
            dismissed={bannerDismissed}
            onDismiss={onBannerDismiss}
            onPrimaryAction={onBannerPrimaryAction}
          />
        ) : null}

        {/* Header: statement summary (+ the no-WHERE badge) + close */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            {headerDml ? (
              // No summary tooltip here: the name is a picker with its own hint.
              // No `truncate` (overflow:hidden) either: focus returning to the
              // picker would scroll the clipped row sideways and cut the icon
              // off — the name truncates inside its own trigger.
              <span
                className={summaryClass}
                data-testid="vqe-compact-summary"
                data-destructive-intent={dmlIntent ?? undefined}
              >
                {summaryIcon}
                <CompactDmlHeaderTarget
                  summary={summary}
                  dml={headerDml}
                  bufferText={model.bufferText}
                  options={options}
                  editContext={editContext}
                />
              </span>
            ) : (
              <VqeTooltip content={summary} shrink>
                <span
                  className={summaryClass}
                  data-testid="vqe-compact-summary"
                  data-destructive-intent={dmlIntent ?? undefined}
                >
                  {summaryIcon}
                  <span className="truncate">{summary}</span>
                </span>
              </VqeTooltip>
            )}
            {dmlOutline && dmlOutline.targetText !== null ? (
              <DmlNoWhereBadge
                verb={dmlIntent === 'delete' ? 'DELETE' : 'UPDATE'}
                // "UPDATE orders" → "orders"; a verb-only header falls back to the outline.
                targetText={
                  headerNamesTarget ? summary.slice(summary.indexOf(' ') + 1) : dmlOutline.targetText
                }
                scope={dmlWhereScope(dmlOutline)}
              />
            ) : null}
          </div>
          {/* min-w-0 lets the identity badge's database name truncate under
              width pressure instead of overflowing (its own text-wrap is
              disabled — see EngineIdentityBadge). */}
          <div className="flex min-w-0 items-center gap-1">
            {/* A6/DML-31/DML-34 — engine/database identity, always mounted (a
                general safety cue, not DML-only) so a compact edit far from the
                status bar still shows which engine/database it targets. */}
            <EngineIdentityBadge
              variant="compact"
              engineId={engineId}
              database={database}
              connectionName={connectionName}
              connectionHost={serverHost}
            />
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
              data-testid="vqe-compact-close"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Drilled breadcrumb — sky-tinted so it reads as the active drill context. */}
        {active && drilledCtx ? (
          <button
            type="button"
            onClick={() => drill.pop()}
            className="flex items-center gap-1 self-start rounded-md bg-sky-100 px-2 py-1 text-sky-700 hover:bg-sky-200 dark:bg-sky-900/40 dark:text-sky-300 dark:hover:bg-sky-900/60"
            data-testid="vqe-compact-drill-back"
            aria-label="Back to parent query"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            <span className="text-[11px] font-medium">{active.label}</span>
          </button>
        ) : null}
      </div>

      {/* (b-DDL) The DDL arm owns the WHOLE area below the header: its own
          scroll body plus ONE pinned action row (`CompactDdlEditor`'s shell).
          It is a sibling of — never inside — the DML scroll body, and it
          replaces the (c) footer block entirely: a DDL statement has no
          buffer-preview to show and nothing to "confirm", so the popover's
          old bottom "Close" row is gone. The header × is the close
          affordance. */}
      {ddlCard ? (
        ddlBody ?? (
          <ScrollFade
            className="min-h-0 flex-1"
            scrollClassName="overflow-x-hidden px-4 py-3"
            testId="vqe-compact-scroll-fade"
          >
            <CompactDdlCard
              model={ddlCard}
              onEditAsSql={onEditAsSql}
              onDesignTable={onDesignTable}
              connectionId={connectionId}
            />
          </ScrollFade>
        )
      ) : (
        <>
          {/* (b) Scrolling body — ONLY the clause sections. `ScrollFade` owns
              the `overflow` container and paints a bottom fade while more
              content sits below the fold. `overflow-x-hidden` keeps long
              SET/WHERE expressions from growing a horizontal scrollbar
              (DML-18). */}
          <ScrollFade
            className="min-h-0 flex-1"
            scrollClassName="overflow-x-hidden px-4 py-3"
            testId="vqe-compact-scroll-fade"
          >
            {active && drilledCtx ? (
              <CompactNestedSections
                editContext={drilledCtx}
                options={options}
                subqueryCtx={buildCompactSubqueryCtx(
                  drilledCtx.statementId,
                  tabId,
                  drill,
                  onOpenFull,
                )}
                onOpenFull={onOpenFull}
                onDegradedChange={body.setNestedDegraded}
              />
            ) : mode === 'update' && model.updateOutline ? (
              <CompactUpdateSections
                bufferText={model.bufferText}
                outline={model.updateOutline}
                options={options}
                editContext={editContext}
                showTargetBanner={!headerNamesTarget}
              />
            ) : mode === 'delete' && model.deleteOutline ? (
              <CompactDeleteSections
                bufferText={model.bufferText}
                outline={model.deleteOutline}
                options={options}
                editContext={editContext}
                showTargetBanner={!headerNamesTarget}
              />
            ) : mergeOutline && editContext && model.statement ? (
              <CompactMergeUnsupportedNotice
                engine={options.engine}
                outline={mergeOutline}
                bufferText={model.bufferText}
                statementRange={model.statement.range}
                editContext={editContext}
                options={options}
                onOpenFull={onOpenFull}
                onEditAsSql={onEditAsSql}
              />
            ) : model.degrade.degraded || !model.outline ? (
              <CompactDegradeNotice
                reason={model.degrade.degraded ? model.degrade.reason : 'unsupported'}
                message={
                  model.degrade.degraded
                    ? model.degrade.message
                    : vqeCopy.degrade.statementFallback
                }
                onOpenFull={onOpenFull}
              />
            ) : (
              <CompactEditSections
                bufferText={model.bufferText}
                outline={model.outline}
                options={options}
                editContext={editContext}
                subqueryCtx={baseSubqueryCtx}
                cteOutline={model.cteOutline}
                // Add CTE on the FROM card's action line while the main query
                // has no WITH (same placement as the full tab); once a WITH
                // exists the CompactCteStrip's own add button takes over.
                cteAddAction={
                  mode === 'select' && !active && !model.degrade.degraded && model.outline &&
                  !model.cteOutline?.hasWith && editContext && editContext.service.getSchemaSnapshot() ? (
                    <CteAddPopover editContext={editContext} />
                  ) : undefined
                }
              />
            )}
          </ScrollFade>

          {/* (c) Pinned footer block — SIBLING of the scroll body, so it is
              always visible at every content height and resize. Carries the
              universal live SQL preview (open by default) + the
              `CompactVqeFooter`. Open-full is hidden while a degrade card owns
              the single CTA (§1.5); Done stays unconditional. DDL never
              reaches here — see the (b-DDL) branch. */}
          <div
            // `relative` keeps the pinned footer painting (and hit-testing)
            // above the positioned scroll body even if body content ever
            // overflows its box again — the 2026-07-16 spill painted clause
            // cards over this footer and made the preview toggle unclickable.
            className="relative flex shrink-0 flex-col gap-2 border-t border-border bg-popover px-4 py-2"
            data-testid="vqe-compact-footer-region"
          >
            <CompactSqlPreview sql={preview} />
            {!hideFooterActions && (
              <CompactVqeFooter
                showOpenFull={showOpenFull}
                primaryCta={showOpenFull ? 'done' : 'open-full'}
                onOpenFull={onOpenFull}
                onClose={onClose}
                openFullTooltip={openFullWindowHint}
                status={editStatus}
                // "Run on change" — SELECT only (never auto-runs DML). Mirrors
                // the Settings → UI → Visual editor switch; both write one setting.
                runOnChange={runOnChange && mode === 'select' && !ddlCard ? runOnChange : undefined}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
